import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { LoadingButton } from "@mui/lab";
import { Card, CardContent, Divider, useTheme } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  BreadCrumbs,
  CardHeaderWidget,
  DataTableWidget,
  DialogDelete,
  DialogState,
  EditionAreaWidget,
  IconActionTableDelete,
  IconActionTableDetail,
  IconActionTableEdit,
  IconActionTableState,
  LabelChip,
  ModalWidgetDetailTable,
  SearchBasicTable,
} from "../../../components";
import {
  alphabeticalSort,
  concatObjectConfigDatatable,
  handleActionDelete,
  handleActionEditNew,
  handleActionState,
} from "../../../helpers";

import SelectMultipleWidgetFilter from "components/SelectMultipleWidgetFilter";
import { debounce, values } from "lodash";
import { useForm } from "react-hook-form";
import viewsTexts from "../../../common/viewsTexts.json";
import { AxiosContext } from "../../../context/AxiosContext";
import { Context } from "../../../context/Context";
import { BsCol, BsContainer, BsRow } from "../../../layouts/components";
import { isGarcesFruitUser } from "../helperUsers";
import UserEdit from "../UserEdit";

const useStyles = makeStyles((theme) => ({
  tableContainerStyle: {
    [theme.breakpoints.up("sm")]: {
      paddingRight: 34,
    },
  },
  selectStyle: {
    marginTop: 0,
    marginBottom: 0,
  },
  divider: {
    width: "1px",
    height: "28px",
    marginLeft: "20px",
    marginRight: "20px",
    backgroundColor: theme.palette.primary.main,
    opacity: 0.4,
  },
  actionColumn: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
  },
}));

const UserList = () => {
  const theme = useTheme();
  const axiosContext = useContext(AxiosContext);

  const { userId } = useContext(Context);
  const [loadingDataTableState, setLoadingDataTableState] = useState(false);
  const [loadingFilterState, setLoadingFilterState] = useState(false);
  const [tableDataState, setTableDataState] = useState([]);
  const [openDialogDeleteState, setOpenDialogDeleteState] = useState(false);
  const [openDialogStateState, setOpenDialogStateState] = useState(false);
  const [limitState, setLimitState] = useState(10);
  const [sortState, setSortState] = useState({
    key: "name",
    option: "asc",
  });
  const [pageState, setPageState] = useState(1);
  const [countState, setCountState] = useState(0);
  const [editionState, setEditionState] = useState(false);
  const [rowToHandleState, setRowToHandleState] = useState("");
  const [rowToEditState, setRowToEditState] = useState("");
  const [modalState, setModalState] = useState({
    openDetail: false,
    detailModal: "",
  });
  const [clientDataState, setClientDataState] = useState([]);
  const [profileDataState, setProfileDataState] = useState([]);
  const [filterTextUsernameState, setFilterTextUsernameState] = useState(null);
  const [usernameState, setUsernameState] = useState("");

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm();

  const useClient = watch("client");
  const useProfile = watch("profile");

  const valuesToQuery = useMemo(() => {
    if (isGarcesFruitUser(userId)) {
      return {
        client: "Garces_Fruit",
        profile: useProfile ? useProfile.id : null,
      };
    }
    return {
      client: useClient ? useClient.id : null,
      profile: useProfile ? useProfile.id : null,
    };
  }, [useClient, useProfile]);

  const classes = useStyles();

  const customStyles = {
    headCells: {
      style: {
        color: "white",
        backgroundColor: theme.palette.primary.light,
        "&: nth-last-child(1)": {
          backgroundColor: theme.palette.primary.main,
          borderBottom: "4px solid",
          borderColor: theme.palette.secondary.light,
        },
        div: {
          whiteSpace: "normal",
          wordWrap: "break-word",
        },
      },
    },
  };

  const searchFunc = async (filterText, type) => {
    if (filterText !== "" && type === "username") {
      setFilterTextUsernameState(filterText);
    }
    if (filterText === "" && type === "resetUser") {
      setFilterTextUsernameState(null);
      debouncedSearch("");
    }
  };

  const [resetPagination, setResetPagination] = useState(false);

  // Función para manejar el cambio en el campo de búsqueda
  const handleSearchChange = (value) => {
    setUsernameState(value);
    setPageState(1);
    setResetPagination((prev) => !prev);
    debouncedSearch(value, 1);
  };

  // Función para manejar el envío del formulario
  const onSubmit = async (values) => {
    let data = {};
    data = {
      client: values.client ? values.client.id : "",
      profile: values.profile ? values.profile.id : "",
    };
    if (isGarcesFruitUser(userId)) {
      data = {
        client: "Garces_Fruit",
        profile: useProfile ? useProfile.id : null,
      };
    }
    setPageState(1);
    setResetPagination((prev) => !prev);
    queryData(1, sortState, undefined, data);
  };

  // Función para realizar la búsqueda con debounce
  const debouncedSearch = useCallback(
    debounce((value, page = 1) => {
      queryData(page, sortState, value, valuesToQuery);
    }, 500),
    [useClient, useProfile]
  );

  // Función para realizar la consulta de datos
  async function queryData(
    page = pageState,
    sort = sortState,
    searchTerm = usernameState,
    values = {}
  ) {
    setLoadingDataTableState(true);
    try {
      let objQuery = {
        client: values.client ? values.client : null,
        profile: values.profile ? values.profile : null,
        username: filterTextUsernameState,
        search: searchTerm,
        page: page,
        limit: limitState,
        sortColumn: sort ? sort.key : sortState.key,
        sortOrder: sort ? sort.option : sortState.option,
      };

      if (isGarcesFruitUser(userId)) {
        objQuery.client = "63ff5c7343f82fe93544ec64";
      }

      // console.log(objQuery, "<------ objQuery");

      const response = await axiosContext.authAxios.post(
        "v1/user/list-users",
        objQuery
      );

      const { data, status } = response;
      if (status === 200) {
        setTableDataState(data.data);
        setCountState(data.count);
        if (page !== pageState) setPageState(page);
        setSortState(sort);
      }
    } catch (err) {
      setTableDataState([]);
    } finally {
      setLoadingDataTableState(false);
    }
  }

  // Función para manejar la eliminación de un usuario
  const DialogDeleteHandler = (row) => {
    setRowToHandleState(row);
    setEditionState(false);
    setOpenDialogDeleteState(!openDialogDeleteState);
  };

  // Función para confirmar la eliminación de un usuario
  const deleteActionDialogHandler = async () => {
    try {
      let objDel = { id: rowToHandleState.id };

      const responseDel = await axiosContext.authAxios.post(
        "v1/user/delete-user",
        objDel
      );

      if (responseDel.status === 200) {
        const newUsers = handleActionDelete(rowToHandleState, tableDataState);
        setTableDataState(newUsers);
        setCountState(countState - 1);
      }
    } catch (err) {}
  };

  // Función para manejar el cambio de estado de un usuario
  const DialogStateHandler = (row) => {
    setEditionState(false);
    setRowToHandleState(row);
    setOpenDialogStateState(!openDialogStateState);
  };

  // Función para confirmar el cambio de estado de un usuario
  const stateActionDialogHandler = async () => {
    try {
      let objSta = { id: rowToHandleState.id };

      const responseSta = await axiosContext.authAxios.post(
        "v1/user/update-active-user",
        objSta
      );

      if (responseSta.status === 200) {
        const newUsers = handleActionState(rowToHandleState, tableDataState);
        setTableDataState(newUsers);
      }
    } catch (err) {}
  };

  // Función para cerrar el área de edición
  const closeEditionAreaHandler = () => {
    setEditionState(false);
  };

  // Función para manejar la edición de un usuario
  const editActionHandler = (newObject) => {
    const newUsers = handleActionEditNew(newObject, tableDataState);
    setTableDataState(newUsers);
  };

  // Función para manejar el ordenamiento de columnas en la tabla
  const handleOnSort = async (column, sortDirection) => {
    let nameColumn = column.sortField;

    if (nameColumn !== null) {
      queryData(
        pageState,
        {
          key: nameColumn,
          option: sortDirection,
        },
        undefined,
        valuesToQuery
      );
    }
  };

  // Función para manejar el cambio de página en la tabla
  const handleChangePagination = (page) => {
    queryData(page, sortState, usernameState, valuesToQuery);
  };

  // Función para manejar el cambio de filas por página en la tabla
  const handleChangeRows = async (currentRowsPerPage, currentPage) => {
    setLoadingFilterState(true);
    try {
      let objQuery = {
        page: currentPage,
        limit: currentRowsPerPage,
        sort: sortState,
      };

      const response = await axiosContext.authAxios.post(
        "v1/user/list-users",
        objQuery
      );
      const { data, status } = response;

      if (status === 200) {
        setTableDataState(data.data);
        setLimitState(currentRowsPerPage);
      }
    } catch (err) {
      setTableDataTable([]);
    } finally {
      setLoadingFilterState(false);
    }
  };

  // Función para cerrar el modal de detalles
  const handleCloseModal = () => {
    setModalState({
      ...modalState,
      openDetail: false,
    });
  };

  // Función para obtener la lista de clientes
  const getClientsAsync = useCallback(async () => {
    setLoadingFilterState(true);
    try {
      let response = await axiosContext.authAxios.post(
        // objectQuery,
        "v1/client/list-clients",
        { limit: 500 }
      );
      const { data, status } = response;

      if (status === 200) {
        setClientDataState(alphabeticalSort(data.data, "label"));
      }
    } catch (error) {
    } finally {
      setLoadingFilterState(false);
    }
  }, [axiosContext]);

  // Función para obtener la lista de perfiles
  const getProfilesAsync = useCallback(async () => {
    try {
      let response = await axiosContext.authAxios.post(
        "v1/profile/list-profiles"
      );

      const { data, status } = response;

      if (status === 200) {
        let dataProfiles = data.data;
        if (isGarcesFruitUser(userId)) {
          dataProfiles = dataProfiles.filter(
            (profile) => profile.value !== "Administrador_Panel"
          );
        }
        setProfileDataState(alphabeticalSort(dataProfiles, "label"));
      }
    } catch (error) {}
  }, [axiosContext]);

  // Efecto para cargar clientes y perfiles al montar el componente
  useEffect(() => {
    getClientsAsync();
    getProfilesAsync();
  }, []);

  useEffect(() => {
    queryData();
  }, []);

  const subHeaderComponentMemo = (
    <div className="flex items-center flex-wrap gap-2">
      <SearchBasicTable
        resetFunc={() => {
          searchFunc("", "resetUser");
          setUsernameState("");
        }}
        id="username"
        valuetext={usernameState}
        control={control}
        placeholder="Nombre/Apellido o User"
        filterFunc={() => {}}
        onFilter={(value) => handleSearchChange(value)}
        hasIcon={false}
        className="my-4"
      />
      <hr
        className={`${classes.divider} hidden lg:block`}
        orientation="vertical"
      />
      <form
        autoComplete="on"
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col justify-center items-center gap-4 md:gap-2 md:flex-row "
      >
        {!isGarcesFruitUser(userId) && (
          <SelectMultipleWidgetFilter
            errors={errors.client}
            loading={loadingFilterState}
            control={control}
            labelText="Clientes"
            name="client"
            placeholder="Seleccionar Clientes"
            display="vertical"
            variant="outlined"
            minheight={false}
            isClearable={true}
            isMulti={false}
            isSearchable
            onlyOptionValue={false}
            defaultValue=""
            options={clientDataState}
          />
        )}
        <SelectMultipleWidgetFilter
          errors={errors.profile}
          loading={loadingFilterState}
          control={control}
          labelText="Perfiles"
          name="profile"
          placeholder="Seleccionar Perfiles"
          display="vertical"
          variant="outlined"
          minheight={false}
          isClearable={true}
          isMulti={false}
          isSearchable
          onlyOptionValue={false}
          defaultValue=""
          options={profileDataState}
        />
        <LoadingButton
          variant="contained"
          type="submit"
          color="primary"
          aria-label="filter"
          size="large"
          loading={false}
        >
          <ArrowForwardIosIcon />
        </LoadingButton>
      </form>
    </div>
  );

  const columnsConfig = useMemo(
    () => [
      {
        name: "Nombres",
        selector: (row) => (row.name ? row.name : row.username),
        sortField: "name",
        sortable: true,
        id: 1,
      },
      {
        name: "Apellidos",
        selector: (row) => row.lastname,
        sortField: "lastname",
        sortable: false,
        id: 2,
      },
      {
        name: "Rut",
        selector: (row) => row.rut,
        sortField: "rut",
        cell: (row) => (row.rut ? row.rut : "-"),
        sortable: true,
        id: 3,
      },
      {
        name: "E-mail",
        selector: (row) => row.email,
        sortField: "email",
        sortable: false,
        id: 4,
      },
      {
        name: "Username",
        selector: (row) => row.username,
        sortField: "username",
        sortable: false,
        id: 5,
      },
      {
        name: "Perfil",
        selector: (row) => row.profile,
        sortField: "profile",
        cell: (row) => row.profile?.label || "--",
        sortable: false,
        id: 6,
      },
      {
        name: "Cliente",
        selector: (row) => row.client,
        sortField: "client",
        cell: (row) => row.client?.label || "--",
        sortable: true,
        id: 7,
      },
      {
        name: "Estado",
        selector: (row) => row.active,
        cell: (row) => <LabelChip label={row.active ? "Activo" : "Inactivo"} />,
        sortable: false,
        id: 8,
      },
    ],
    []
  );

  const actionsConfig = [
    {
      name: "Acciones",
      width: "200px",
      id: 9,
      cell: (row) => (
        <div className={classes.actionColumn}>
          <IconActionTableDetail
            handleActionDetail={() => {
              setModalState({
                ...modalState,
                openDetail: true,
                detailModal: row,
              });
            }}
          />
          <IconActionTableEdit
            handleActionEdit={() => {
              setRowToEditState(row);
              setEditionState(true);
            }}
          />
          <IconActionTableState
            rowState={row.active}
            handleActionState={() => DialogStateHandler(row)}
          />
          {!isGarcesFruitUser(userId) && (
            <IconActionTableDelete
              handleActionDelete={() => DialogDeleteHandler(row)}
            />
          )}
        </div>
      ),
    },
  ];
  const actions = concatObjectConfigDatatable(actionsConfig[0]);
  const columns = columnsConfig.concat(actions);

  return (
    <div className="form-style">
      <BsContainer fluid class="px-0 px-md-3">
        <BsRow class="no-gutters">
          <BsCol class="col-md-12">
            {editionState && (
              <EditionAreaWidget
                component={
                  <UserEdit
                    rowEdit={rowToEditState}
                    closeFunc={closeEditionAreaHandler}
                    editFunc={editActionHandler}
                  />
                }
                visible={editionState}
              />
            )}
            <Card elevation={0}>
              <CardHeaderWidget
                title={viewsTexts.titles.user.list.title}
                subheader={<BreadCrumbs subTitle="" />}
              />
              <CardContent>
                <div
                  style={{
                    minHeight: "300px",
                  }}
                >
                  <DataTableWidget
                    totalRows={countState}
                    handleOnSort={handleOnSort}
                    fixedHeader={true}
                    fixedHeaderScrollHeight={"590px"}
                    changePagination={handleChangePagination}
                    rowsChange={handleChangeRows}
                    columns={columns}
                    sortField="1"
                    filteredItems={tableDataState}
                    subHeaderComponentMemo={subHeaderComponentMemo}
                    loading={loadingDataTableState}
                    loadingCount={loadingDataTableState}
                    customStyles={customStyles}
                    moduleName="Usuarios"
                    resetPaginationToggle={resetPagination}
                  />
                </div>
                <ModalWidgetDetailTable
                  size="modal-50"
                  closeFunc={handleCloseModal}
                  openDetail={modalState.openDetail}
                  detailModal={modalState.detailModal}
                  moduleName="Usuarios"
                />
                <DialogState
                  openDialog={openDialogStateState}
                  rowState={rowToHandleState}
                  actionState={stateActionDialogHandler}
                  variant="success"
                  openState={setOpenDialogStateState}
                  moduleName="Usuarios"
                />
                <DialogDelete
                  openDialog={openDialogDeleteState}
                  rowDelete={rowToHandleState}
                  actionDelete={deleteActionDialogHandler}
                  variant="success"
                  openState={setOpenDialogDeleteState}
                  moduleName="Usuarios"
                />
              </CardContent>
            </Card>
          </BsCol>
        </BsRow>
      </BsContainer>
    </div>
  );
};

export default UserList;
